// src/contexts/DeployAttemptContext.jsx
//
// Command Center slice S2b — the deploy attempt provider (spec V1.2 §2, §5;
// corrections C2, C3, C4; rulings R-S2b-1 … R-S2b-8). It owns, for the hub,
// the one attempt record, the hold slot's record, the committed beat, the
// due-idea screen, the root deploy ceremony and every ownership check between
// a press and its result.
//
// WHERE IT LIVES (ruling R-S2b-1, a deliberate deviation from spec §2's
// main.jsx placement): App's unified return wraps the screen router in it.
// App does not remount across screens or widths, so the attempt, its beat and
// its ceremony outlive both, and the app's entry file stays untouched. App
// hands its callbacks in as props — that is the bridge — and the provider
// checks the captured uid, auth epoch and attempt epoch immediately before
// calling each one.
//
// FLAG OFF (spec §4): the hub flag is read once, at mount, through
// isCommandCenterHubOn(). False, this is a pass-through — it renders its
// children and nothing else: no reducer work, no subscription, timer or
// listener, no ceremony. A later false (a runtime disable, injected by its
// rows as `hubEnabled`; no live toggle exists) cancels an uncommitted hold and
// refuses new attempts, while a request already committed keeps its record and
// its ceremony through resolution.
//
// THE RECORD. hub/selectors/attemptReducer.js, over S2a's vocabulary, held in
// a ref so a second press in the same tick sees the first one's guard (spec
// §5.3). Before every `begin` the provider asks the hub's own §3.1 table
// (deployState.js): canStart for a tap, canCommit at a hold's threshold, the
// selector's `retry` action for a Retry. The table's reader inputs are the
// hub's (hub/useHubDeploy.js registers them); the identity is read now from
// the one source (services/authIdentity.js); attempt, hold, cooldown, the clock
// and the open overlays are the provider's.
//
// OWNERSHIP (spec §5.3, C2). Every attempt captures uid, auth epoch and attempt
// epoch. After every await, and before the due-idea screen, the ceremony, the
// target report, App's handoff, a navigation and every result shown, both
// must still match. An identity change cancels a charge and revokes a beat in
// its own task, orphans the attempt, closes its ceremony and screen, drops its
// reads and the matching ceremony stash, and is cleaned up at once: a late old
// promise finds nothing to change. The server battle a request may already have
// created for its owner is never touched.
//
// THE HOLD (spec §5.5, C4). The hub's controls claim through `holdSeam`
// (useHoldToDeploy.js, THE HUB'S SEAM): a claim is admitted by canStart before
// the slot grants it, with the input and this provider's hold epoch; the
// threshold commits only through canCommit and the slot together; the
// provider is the controls' beatOwner, so a committed beat survives unmount
// and a width change, and is run — or released — on every exit (R2 FR2-5).
// A charge is cancelled synchronously (R2 FR2-4) by an identity change, a
// runtime disable, an overlay opening and an area change. Each frame's charge
// goes to an owner-tagged store (`progress`), read by the control and the
// status line only.
//
// THE DEPLOY (ruling R-S2b-4): the shells' steps, owned here — claim/commit or
// tap; checkDueIdea and its one screen (P1c E1; a Retry skips it); the
// ceremony opened; deployAgent with `isCurrent`; the service result; the
// ceremony's phases through onPhaseChange with the attempt id. With the
// ceremony off a created battle navigates at once (App's handoff, as today)
// and an existing one opens only its fetched owned document.
//
// RECONCILIATION (rulings R-S2b-5, R-S2b-6). An existing answer gets one owned
// targeted read of the server's id per attempt (services/ownedBattleRead.js),
// with a deadline and a 30 s Retry cooldown. An uncertain one is resolved only
// by reconciliation: Check deployment runs the target-scoped verifier (found →
// existing; clear → a Retry once the cooldown has passed; error → still
// uncertain); a ceremony whose preserved recovery gates admitted a battle
// (phase `reveal`) is read once to learn its id → created, recovered; a
// ceremony that confirmed no battle (`error`, tone `confirmed`) is clear.
// Nothing replays automatically.

import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ATTEMPT_ACTION_TYPES as T, attemptReducer, cooldownFor, initialAttemptState } from '../components/CommandCenter/hub/selectors/attemptReducer';
import { ATTEMPT_ACTIONS, ATTEMPT_PHASES, CEREMONY_PHASES, ERROR_TONES, HOLD_PHASES, RECONCILIATION, RESULT_KINDS, realId } from '../components/CommandCenter/hub/selectors/attemptVocabulary';
import { canCommit, deriveHubDeployState } from '../components/CommandCenter/hub/selectors/deployState';
import { authInputOf } from '../components/CommandCenter/hub/selectors/readerInputs';
import { inertSiblings, takeOwedFocus, tryFocus } from '../components/CommandCenter/hub/overlayStack';
import { lastDeployedAtMs } from '../components/CommandCenter/hub/hubDate';
import useDeployTargetProgress from '../hooks/useDeployTargetProgress';
import { readAuthIdentity, subscribeAuthIdentity } from '../services/authIdentity';
import { deployAgent } from '../services/agentDeploy';
import { checkDueIdea } from '../services/dueIdeaCheck';
import { findActiveBattleForAgent } from '../services/agentBattleVerify';
import { readOwnedBattle } from '../services/ownedBattleRead';
import { claimHold, commitHold, currentHold, releaseHold } from '../hooks/holdClaim';
import { LOCK_BEAT_MS } from '../hooks/useHoldToDeploy';
import { isCommandCenterHubOn, isDeployCeremonyOn } from '../config/featureFlags';
import { CMD } from '../components/Dashboard/commandUI';
import { deployResultFor, isOrphanedResult } from '../components/Dashboard/deployResult';
import { isInProgressRefusal } from '../constants/deployOutcomes';
import * as ceremonyTiming from '../components/Dashboard/deployCeremony/ceremonyTiming';
import DeployCeremony from '../components/Dashboard/deployCeremony/DeployCeremony';
import DueIdeaInterstitial from '../components/Dashboard/deployCeremony/DueIdeaInterstitial';

/** The owned targeted read's deadline, and the wait before its Retry (spec §6: "a 30-second per-attempt cooldown"). */
export const EXISTING_READ_DEADLINE_MS = 8000;
export const EXISTING_READ_RETRY_MS = 30000;
/** The target-scoped verifier's deadline for Check deployment (the same bound as the other hub operations, spec §6). */
export const RECONCILE_DEADLINE_MS = 8000;

/** The hydration's statuses (the hub's card reads them). */
export const EXISTING_READ = Object.freeze({ LOADING: 'loading', FOUND: 'found', ERROR: 'error' });

const DeployAttemptContext = createContext(null);

/**
 * The two overlays this provider draws portal their dialog straight into <body> (DueIdeaInterstitial.jsx,
 * DeployCeremony.jsx), each under a class of its own on that root. Their root is what stays interactive while the rest of
 * the page is inert (S8 review S8-14). Null when it is not a child of <body> (the provider suites' in-place stand-ins).
 */
const OVERLAY_ROOT_CLASS = Object.freeze({ due: 'due-idea-interstitial', ceremony: 'deploy-ceremony-overlay' });
function overlayRootOf(overlayKey) {
  const kind = overlayKey.slice(0, overlayKey.indexOf(':'));
  const roots = document.querySelectorAll(`body > .${OVERLAY_ROOT_CLASS[kind]}`);
  return roots.length ? roots[roots.length - 1] : null;
}

/** The provider's `{ snapshot, api }`, or null with the hub flag off or outside a provider. */
export function useDeployAttempt() {
  return useContext(DeployAttemptContext);
}

/**
 * The owner-tagged hold progress (spec §5.5): `{ controlId, holdToken, progress }` of the charge in flight, or null.
 * Read only by the control's status line; a Games row never re-renders per frame because nothing else subscribes.
 */
export function useHoldProgress(store) {
  const subscribe = store ? store.subscribe : noopSubscribe;
  const get = store ? store.get : nullSnapshot;
  return useSyncExternalStore(subscribe, get, get);
}
const noopSubscribe = () => () => {};
const nullSnapshot = () => null;

function createProgressStore() {
  let value = null;
  const listeners = new Set();
  const notify = () => { for (const listener of [...listeners]) listener(); };
  return {
    get: () => value,
    subscribe: (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    set(next) { value = Object.freeze(next); notify(); },
    clear(holdToken) {
      if (value === null || (holdToken != null && value.holdToken !== holdToken)) return;
      value = null;
      notify();
    },
  };
}

const finite = (n) => typeof n === 'number' && Number.isFinite(n);
const TIMED_OUT = Symbol('timed-out');

/**
 * The provider's state machine, outside React (the component below renders it). Every method is synchronous up to
 * its first await; every continuation re-checks its capture.
 */
export function createDeployRuntime({ getBridge, notify, now = () => Date.now() }) {
  const first = readAuthIdentity();
  let state = initialAttemptState({ uid: first.resolved ? first.uid : null, authEpoch: first.authEpoch });
  let identity = first;
  let revoked = false;
  let inputsGetter = null;
  let holdEpoch = 0;
  let attemptSeq = 0;
  let charge = null; // { token, controlId, cancel } — the charging hold
  let committed = null; // { token, controlId, agent, ceremonyAgent } — the hold committed at its threshold
  let beat = null; // { token, run, timer } — the committed beat this provider owns
  let run = null; // the attempt's runtime: { attemptId, attemptEpoch, capturedUid, authEpoch, agent, ceremonyAgent }
  let ceremony = null; // { attemptId, key, visible, deployResult, targetAgentId }
  let due = null; // { attemptId, due, proceed, settle }
  let hydration = null; // { key, attemptId, battleId, status, battle, retryAt }
  let hydrationRead = null; // { key, settled } — the read in flight, which a second ask waits on (Q-S2b-5)
  let evidenceSeen = null; // the attempt id whose target document the evidence listener has read once (FRB-7)
  let recovered = null; // { attemptId, battle } — the document a recovered creation enters
  let version = 0;
  let cached = null;
  const timers = new Set();
  const progress = createProgressStore();

  const changed = () => { version += 1; notify(); };
  const later = (fn, ms) => {
    const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
    timers.add(id);
    return id;
  };
  const cancelLater = (id) => { if (id != null) { clearTimeout(id); timers.delete(id); } };
  const within = (promise, ms) => {
    let timer = null;
    return Promise.race([
      promise,
      new Promise((resolve) => { timer = later(() => resolve(TIMED_OUT), ms); }),
    ]).finally(() => cancelLater(timer));
  };

  const apply = (action) => {
    const next = attemptReducer(state, action);
    if (next !== state) { state = next; changed(); }
    return next;
  };

  const bridge = () => getBridge() || {};
  const overlayOpen = () => Boolean(ceremony?.visible) || due !== null;

  /** A capture is current only while the identity, its epoch and the attempt epoch are all still its own (C2). */
  const currentFor = (r) => () => {
    if (revoked || !r) return false;
    const id = readAuthIdentity();
    return id.resolved === true && id.uid === r.capturedUid && id.authEpoch === r.authEpoch
      && state.attemptEpoch === r.attemptEpoch && state.attempt?.attemptId === r.attemptId;
  };

  // ── the selector's inputs at this instant ───────────────────────────────────
  function admission({ exemptOverlay = false } = {}) {
    const hub = inputsGetter ? inputsGetter() : null;
    if (!hub || !hub.inputs) return null;
    const auth = authInputOf(readAuthIdentity());
    const agentDoc = hub.inputs.agent?.agent ?? null;
    const target = state.attempt?.targetAgentId ?? agentDoc?.id ?? null;
    // The Scouting Board's own deploy is inside its modal (spec §10): the board is the one overlay it may sit under.
    const hubModal = exemptOverlay ? hub.hubModalDepth > 1 : hub.hubModalOpen === true;
    return {
      inputs: Object.freeze({
        ...hub.inputs,
        auth,
        attempt: state.attempt,
        hold: state.hold,
        cooldown: auth?.signedIn === true ? cooldownFor(state, auth.uid, target) : null,
        now: now(),
        modalOpen: hubModal || overlayOpen(),
      }),
      agent: agentDoc,
      ceremonyAgent: hub.ceremonyAgent ?? null,
      liveBattles: Array.isArray(hub.liveBattles) ? hub.liveBattles : [],
    };
  }

  // ── the hold seam (useHoldToDeploy.js THE HUB'S SEAM) ───────────────────────
  function claim(controlId, inputId, cancel) {
    if (revoked || state.flagDisabled) return null;
    const a = admission();
    // A denied claim returns before any effect: no frame, timer, progress, sky or request (spec §5.5).
    if (!a || deriveHubDeployState(a.inputs).canStart !== true) return null;
    holdEpoch += 1;
    const token = claimHold(controlId, inputId, holdEpoch);
    if (token === null) return null;
    const next = apply({ type: T.HOLD_CLAIMED, auth: a.inputs.auth, controlId, holdToken: token, holdEpoch });
    if (next.hold?.holdToken !== token) { releaseHold(token); return null; }
    charge = { token, controlId, cancel };
    progress.set({ controlId, holdToken: token, progress: 0 });
    return token;
  }

  function commit(token) {
    if (revoked) return false;
    const a = admission();
    if (!a || !canCommit(token, a.inputs)) return false;
    if (!commitHold(token)) return false;
    const next = apply({ type: T.HOLD_COMMITTED, auth: a.inputs.auth, holdToken: token });
    if (next.hold?.holdToken !== token || next.hold.phase !== HOLD_PHASES.LOCKED) { releaseHold(token); return false; }
    committed = { token, controlId: next.hold.controlId, agent: a.agent, ceremonyAgent: a.ceremonyAgent };
    charge = null;
    return true;
  }

  function release(token) {
    releaseHold(token);
    if (charge?.token === token) charge = null;
    if (committed?.token === token) committed = null;
    progress.clear(token);
    apply({ type: T.HOLD_RELEASED, holdToken: token });
  }

  function report(token, p) {
    const owner = charge?.token === token ? charge : committed?.token === token ? committed : null;
    if (owner && finite(p)) progress.set({ controlId: owner.controlId, holdToken: token, progress: Math.max(0, Math.min(1, p)) });
  }

  const holdSeam = Object.freeze({ claim, commit, release, progress: report });

  /** The committed beat, owned here: it survives the control's unmount and a width change (spec §5.5). */
  const beatOwner = Object.freeze({
    commit(token, runBeat) {
      // Not ours (revoked, or not the hold this provider committed): the hook keeps its own beat.
      if (revoked || committed?.token !== token) throw new Error('[DeployAttempt] no committed hold for this beat');
      if (beat) cancelLater(beat.timer);
      beat = { token, run: runBeat, timer: null };
      beat.timer = later(() => { const b = beat; beat = null; if (b) b.run(); }, LOCK_BEAT_MS);
    },
  });

  /** The synchronous cancel of an uncommitted charge, in the caller's task (R2 FR2-4). */
  function cancelCharging() {
    const c = charge;
    if (!c) return;
    try { c.cancel(); } catch (err) { console.warn('[DeployAttempt] the charge cancel threw:', err?.message || err); }
    // The control's own cancel gives the claim back through release(); a control that could not, is released here.
    if (charge?.token === c.token) release(c.token);
  }

  /** A committed beat not yet run is revoked: its run then deploys nothing and returns its control to idle. */
  function revokeBeat({ runIt = true } = {}) {
    const b = beat;
    if (!b) return;
    beat = null;
    cancelLater(b.timer);
    releaseHold(b.token);
    if (committed?.token === b.token) committed = null;
    progress.clear(b.token);
    if (runIt) { try { b.run(); } catch (err) { console.warn('[DeployAttempt] a revoked beat threw:', err?.message || err); } }
  }

  // ── identity, runtime disable, unmount ──────────────────────────────────────
  function cleanupOrphan() {
    const a = state.attempt;
    if (!a || a.phase !== ATTEMPT_PHASES.ORPHANED) return;
    // Local cleanup of the former owner's attempt (spec §5.4): its screen, ceremony, reads, stash and timers.
    const r = run;
    if (due && due.attemptId === a.attemptId) { const d = due; due = null; d.settle('orphaned'); }
    if (ceremony && ceremony.attemptId === a.attemptId) ceremony = null;
    if (hydration && hydration.attemptId === a.attemptId) hydration = null;
    if (recovered && recovered.attemptId === a.attemptId) recovered = null;
    const battleId = realId(a.result?.battleId);
    if (battleId) { try { bridge().clearCeremonyStash?.(battleId); } catch { /* the stash is App's; nothing to undo here */ } }
    if (r && r.attemptId === a.attemptId) run = null;
    apply({ type: T.ORPHAN_CLEANED, attemptId: a.attemptId, attemptEpoch: a.attemptEpoch });
    changed();
  }

  /** The slot of a hold that already started its request is freed now, not when the old request settles. */
  function releaseInFlightHold() {
    const held = state.hold;
    if (held && held.phase === HOLD_PHASES.LOCKED) releaseHold(held.holdToken);
  }

  function identityChanged(next) {
    if (revoked || !next || next.authEpoch === identity.authEpoch) return;
    identity = next;
    cancelCharging();
    revokeBeat();
    // The new account is admitted on its own readiness, never after the old promise (spec §5.4; review W2-1): the
    // hook's own release, at the old request's settle, is token-scoped and finds nothing then.
    releaseInFlightHold();
    // The former attempt's deadlines go with it (spec §5.4; S8 review S8-12): an owned read's or a deployment check's 8 s
    // timer would only resolve a race its own `isCurrent()` already ignores. Nothing awaits that race after the change.
    for (const id of timers) clearTimeout(id);
    timers.clear();
    apply({ type: T.AUTH_CHANGED, uid: next.resolved ? next.uid : null, authEpoch: next.authEpoch });
    cleanupOrphan();
    // An acknowledged record goes with no orphan to clean: drop what it left for the former owner (review W2-3).
    run = null;
    hydration = null;
    recovered = null;
    ceremony = null;
    changed();
  }

  function setHubEnabled(enabled) {
    if (revoked || enabled === true) return;
    cancelCharging();
    apply({ type: T.FLAG_DISABLED });
  }

  function unmount() {
    if (revoked) return;
    cancelCharging();
    // The owner releases a beat it will never run (R2 FR2-5); nothing runs after the root has gone.
    revokeBeat({ runIt: false });
    releaseInFlightHold();
    if (due) { const d = due; due = null; d.settle('unmounted'); }
    state = attemptReducer(state, { type: T.UNMOUNT });
    revoked = true;
    for (const id of timers) clearTimeout(id);
    timers.clear();
    ceremony = null;
    hydration = null;
    run = null;
    progress.clear(null);
  }

  // ── the deploy ──────────────────────────────────────────────────────────────
  const newAttemptId = () => { attemptSeq += 1; return `attempt_${attemptSeq}_${now()}`; };

  function cooldownFromResult(r, result) {
    const target = state.attempt?.targetAgentId;
    if (!target) return;
    const retryAfterUntil = finite(result?.retryAfter) && result.retryAfter >= 0 ? now() + result.retryAfter * 1000 : null;
    if (retryAfterUntil !== null) {
      apply({ type: T.COOLDOWN_EVIDENCE, ownerUid: r.capturedUid, authEpoch: r.authEpoch, targetAgentId: target, retryAfterUntil });
    }
  }

  /** The recorded `lastDeployedAt` of a deploy target, as the reader or the target's own document reports it. */
  function lastDeployedEvidence({ uid, authEpoch, targetAgentId, lastDeployedAtMs }) {
    if (revoked || !finite(lastDeployedAtMs)) return;
    apply({ type: T.COOLDOWN_EVIDENCE, ownerUid: uid, authEpoch, targetAgentId, lastDeployedAtMs });
  }

  /** The owned active poll, read again because a battle it could show was created or found (spec §6). */
  function refreshActive() {
    try { bridge().refreshActiveBattles?.(); } catch (err) { console.warn('[DeployAttempt] refresh failed:', err?.message || err); }
  }

  /** The evidence listener has read this attempt's target document once (its stamp, if any, already reported). */
  function evidenceRead(attemptId) {
    if (revoked || evidenceSeen === attemptId) return;
    evidenceSeen = attemptId;
    changed();
  }

  async function startAttempt({ hold = null, tap = null, retryOf = null }) {
    if (revoked) return { success: false };
    const auth = authInputOf(readAuthIdentity());
    if (auth?.signedIn !== true) return { success: false };
    const attemptId = newAttemptId();
    const startedAt = now();
    let agent = null;
    let ceremonyAgent = null;
    let next = state;
    if (hold) {
      // Committed at its threshold through canCommit (spec §5.5): the reducer admits only that hold.
      agent = hold.agent;
      ceremonyAgent = hold.ceremonyAgent;
      if (!agent?.id) return { success: false };
      next = apply({ type: T.BEGIN, attemptId, auth, targetAgentId: agent.id, startedAt, controlId: hold.controlId, holdToken: hold.token });
    } else if (retryOf) {
      const a = admission();
      if (!a || state.flagDisabled) return { success: false };
      // The selector offers Retry only when the request that would replace the terminal is admitted now (§5.4).
      if (deriveHubDeployState(a.inputs).action !== ATTEMPT_ACTIONS.RETRY) return { success: false };
      agent = retryOf.agent ?? a.agent;
      ceremonyAgent = retryOf.ceremonyAgent ?? a.ceremonyAgent;
      if (!agent?.id) return { success: false };
      next = apply({ type: T.RETRY, attemptId, priorAttemptId: retryOf.attemptId, priorAttemptEpoch: retryOf.attemptEpoch, auth, now: now(), startedAt });
    } else if (tap) {
      if (state.flagDisabled) return { success: false };
      const a = admission({ exemptOverlay: tap.exemptOverlay === true });
      // A tap can never bypass another control's hold: the table and the reducer both refuse it (spec §5.5).
      if (!a || deriveHubDeployState(a.inputs).canStart !== true) return { success: false };
      agent = a.agent;
      ceremonyAgent = a.ceremonyAgent;
      if (!agent?.id) return { success: false };
      next = apply({ type: T.BEGIN, attemptId, auth, targetAgentId: agent.id, startedAt });
    }
    if (next.attempt?.attemptId !== attemptId) return { success: false };

    const r = Object.freeze({
      attemptId, attemptEpoch: next.attempt.attemptEpoch, capturedUid: auth.uid, authEpoch: auth.authEpoch, agent, ceremonyAgent,
    });
    run = r;
    hydration = null;
    recovered = null;
    const isCurrent = currentFor(r);

    // P1c founder ruling E1: one screen before any deploy call; a Retry skips it (the choice was made).
    ceremonyTiming.startRun();
    if (!retryOf) {
      const dueIdea = await checkDueIdea(agent);
      if (!isCurrent()) return { success: false };
      if (dueIdea) {
        const choice = await new Promise((settle) => {
          due = { attemptId, due: dueIdea, settle, proceed: () => settle('proceed') };
          changed();
        });
        if (due && due.attemptId === attemptId) due = null;
        changed();
        if (choice !== 'proceed' || !isCurrent()) return { success: false };
        ceremonyTiming.startRun();
      }
    }

    const ceremonyOn = isDeployCeremonyOn();
    // The ceremony opens BEFORE the call, seeded pending (the stage machine's OPEN-3 note depends on it).
    if (ceremonyOn) {
      ceremony = { attemptId, key: attemptId, visible: true, deployResult: { status: 'pending' }, targetAgentId: null };
      changed();
    }

    const onTarget = (id) => {
      if (!isCurrent()) return;
      apply({ type: T.TARGET_RESOLVED, attemptId, attemptEpoch: r.attemptEpoch, targetAgentId: id });
      if (ceremony?.attemptId === attemptId) { ceremony = { ...ceremony, targetAgentId: id }; changed(); }
    };
    // App's handoff, checked here immediately before it (C2). App's own checks are R1's — the captured uid against
    // the signed-in one before each of its effects; the attempt and auth epochs are this check's (review W2-4).
    const onCreate = (portfolio, bench, meta) => {
      if (!isCurrent()) return null;
      const create = bridge().onCreateAgentBattle;
      return typeof create === 'function' ? create(portfolio, bench, meta) : null;
    };

    let result;
    try {
      result = await deployAgent(agent.id, onCreate, onTarget, { isCurrent });
    } catch (err) {
      // deployAgent's only throw is before its POST; an untyped result fails closed (uncertain) all the same.
      console.error('[DeployAttempt] deploy threw:', err?.message || err);
      result = { success: false, error: err?.message, postIssued: false, httpStatus: null };
    }
    // An identity change, a newer attempt or the root's unmount: nothing more is shown or done for this one.
    if (!isCurrent()) return result;

    // The ceremony's last report before the answer: a watchdog check may already have ended it on its error.
    const ceremonyBefore = state.attempt?.ceremonyPhase ?? null;
    apply({ type: T.SERVICE_RESULT, attemptId, attemptEpoch: r.attemptEpoch, result });
    cooldownFromResult(r, result);
    if (state.attempt?.phase === ATTEMPT_PHASES.ORPHANED) {
      cleanupOrphan();
      return result;
    }

    const phase = state.attempt?.phase;
    // A created or existing answer that lands after the ceremony's own check already settled on "Couldn't
    // confirm" (its 90 s watchdog fired while the POST was out) cannot reach that latched machine, so the
    // record and the ceremony would disagree. For a hub attempt the answer gets a fresh ceremony instance,
    // whose machine reads it from the start (one vocabulary, spec §5.2; the post-check half of R1 FR-1).
    // A creation that answers while that check is still in flight is remounted too: the latched machine would
    // end it on "Couldn't confirm" (independent review FRB-5).
    const answered = phase === ATTEMPT_PHASES.CREATED || phase === ATTEMPT_PHASES.EXISTING;
    const latchedBefore = ceremonyBefore === CEREMONY_PHASES.ERROR
      || (ceremonyBefore === CEREMONY_PHASES.VERIFYING && phase === ATTEMPT_PHASES.CREATED);
    const remount = latchedBefore && answered;
    const handTheCeremony = () => {
      if (!isCurrent() || ceremony?.attemptId !== attemptId) return;
      // Decided again at hand-off time: an existing answer waits for its owned read, and the watchdog's check
      // can latch the error inside that wait (independent review FRB-3).
      const latchedSince = state.attempt?.ceremonyPhase === CEREMONY_PHASES.ERROR;
      const fresh = remount || (latchedSince && answered);
      ceremony = { ...ceremony, deployResult: deployResultFor(result), ...(fresh ? { key: `${attemptId}:answer` } : {}) };
      changed();
    };
    const shown = ceremonyOn && ceremony?.attemptId === attemptId && ceremony.visible === true;
    if (shown) {
      if (isOrphanedResult(result)) { ceremony = null; changed(); }
      // An existing answer reaches the ceremony once its one owned targeted read has settled (below), so the
      // existing terminal opens the document that read returned — a league battle included, which the
      // terminal's own fallback (the target-scoped verifier) cannot resolve. The read has its own deadline.
      else if (phase !== ATTEMPT_PHASES.EXISTING) handTheCeremony();
    } else if (phase === ATTEMPT_PHASES.CREATED || phase === ATTEMPT_PHASES.REFUSED) {
      // No ceremony on screen to carry a creation to its reveal — the flag is off, or the player closed it while
      // the request was out — so the record closes it itself, and the hub shows the creation with its Enter
      // (spec §5.2: "Deployed · Enter battle"), never a "Finishing" or "Verifying" no ceremony will finish
      // (review W4-1, W4-2). A refusal no ceremony shows is closed the same way, so the hub offers its Dismiss
      // beside Retry (founder ruling Q-S2b-4): nothing else would ever acknowledge it.
      apply({ type: T.CEREMONY_PHASE, attemptId, attemptEpoch: r.attemptEpoch, phase: CEREMONY_PHASES.CLOSED, errorTone: null });
    }

    // The owned active poll learns of a battle only after a created or existing answer; a refusal or an uncertain
    // answer changes no battle the poll could show (spec §6; independent review FRB-8).
    if (answered) refreshActive();

    if (phase === ATTEMPT_PHASES.EXISTING) {
      const settled = hydrate({ open: !ceremonyOn });
      if (ceremonyOn) settled.then(handTheCeremony);
    } else if (phase === ATTEMPT_PHASES.CREATED && !ceremonyOn && state.attempt?.result?.kind === RESULT_KINDS.CREATED) {
      // Ceremony off: App's handoff navigated at once, as today; that navigation acknowledges the terminal. A
      // handoff that threw navigated nowhere, so its creation stays, "Deployed · Enter battle", for the real id.
      dismiss();
    }
    return result;
  }

  // ── the owned targeted read (spec §6, ruling R-S2b-5) ──────────────────────
  /**
   * The battle document an attempt names, read once by its id and owned (services/ownedBattleRead.js): an
   * existing answer's server id, or a creation whose App handoff threw (its real id; review W4-5). Deduplicated
   * by uid + auth epoch + attempt id + battle id; a failed read waits EXISTING_READ_RETRY_MS before its explicit
   * Retry. With `open`, a read that succeeds goes on to open (existing) or enter (created) it (review W4-4).
   * Resolves (never rejects) once the read has settled, or at once when nothing is to be read; an ask while the
   * same read is out resolves when that read settles, so it starts no second one (founder ruling Q-S2b-5).
   */
  function hydrate({ open = false } = {}) {
    const a = state.attempt;
    const r = run;
    if (!a || !r || r.attemptId !== a.attemptId) return Promise.resolve();
    const existing = a.phase === ATTEMPT_PHASES.EXISTING;
    const handoff = a.phase === ATTEMPT_PHASES.CREATED && a.result?.kind === RESULT_KINDS.HANDOFF;
    if (!existing && !handoff) return Promise.resolve();
    const battleId = existing ? realId(a.resolution?.battleId) ?? realId(a.result?.battleId) : realId(a.result?.battleId);
    if (!battleId) return Promise.resolve();
    const key = `${r.capturedUid}:${r.authEpoch}:${a.attemptId}:${battleId}`;
    if (hydration?.key === key) {
      if (hydration.status === EXISTING_READ.LOADING && hydrationRead?.key === key) return hydrationRead.settled;
      if (hydration.status !== EXISTING_READ.ERROR) return Promise.resolve();
      if (finite(hydration.retryAt) && now() < hydration.retryAt) return Promise.resolve();
    }
    hydration = { key, attemptId: a.attemptId, battleId, status: EXISTING_READ.LOADING, battle: null, retryAt: null };
    changed();
    const isCurrent = currentFor(r);
    const failed = () => {
      hydration = { ...hydration, status: EXISTING_READ.ERROR, retryAt: now() + EXISTING_READ_RETRY_MS };
      changed();
    };
    const settled = within(Promise.resolve().then(() => readOwnedBattle(battleId, r.capturedUid)), EXISTING_READ_DEADLINE_MS)
      .then((battle) => (battle === TIMED_OUT ? Promise.reject(new Error('timeout')) : battle))
      .then((battle) => {
        if (!isCurrent() || hydration?.key !== key) return;
        if (battle && battle.id === battleId && battle.ownerId === r.capturedUid) {
          hydration = { ...hydration, status: EXISTING_READ.FOUND, battle };
          changed();
          if (open) {
            if (existing) openGame();
            else enterBattle();
          }
        } else {
          failed();
        }
      }, (err) => {
        if (!isCurrent() || hydration?.key !== key) return;
        console.warn('[DeployAttempt] the battle could not be read:', err?.message || err);
        failed();
      });
    hydrationRead = { key, settled };
    return settled;
  }

  /**
   * The ceremony's existing-terminal Retry (founder ruling Q-S2b-5), through the one owned read above: the same
   * dedup, deadline and 30 s wait as the hub's own Retry. Resolves to what the terminal shows; never rejects.
   */
  function retryExistingRead() {
    const a = state.attempt;
    const r = run;
    if (!a || !r || r.attemptId !== a.attemptId || a.phase !== ATTEMPT_PHASES.EXISTING) {
      return Promise.resolve({ status: 'unavailable', battle: null });
    }
    return hydrate().then(() => {
      const h = hydration;
      const battle = h?.attemptId === a.attemptId && h.status === EXISTING_READ.FOUND ? h.battle : null;
      return battle && currentFor(r)() ? { status: 'found', battle } : { status: 'unavailable', battle: null };
    });
  }

  // ── reconciliation of an uncertain attempt (§5.4) ──────────────────────────
  function reconcile({ admitted = false } = {}) {
    const a = state.attempt;
    const r = run;
    if (!a || a.phase !== ATTEMPT_PHASES.UNCERTAIN || !r || r.attemptId !== a.attemptId) return;
    if (a.reconciliationStatus === RECONCILIATION.PENDING) return;
    const tag = { attemptId: a.attemptId, attemptEpoch: a.attemptEpoch };
    apply({ type: T.RECONCILIATION_PENDING, ...tag });
    const isCurrent = currentFor(r);
    const target = a.targetAgentId;
    within(Promise.resolve().then(() => findActiveBattleForAgent(target)), RECONCILE_DEADLINE_MS)
      .then((found) => {
        if (!isCurrent()) return;
        if (found === TIMED_OUT || !found || typeof found.found !== 'boolean') {
          apply({ type: T.RECONCILIATION_RESULT, ...tag, status: RECONCILIATION.ERROR });
          return;
        }
        if (!found.found) {
          // After the ceremony's own gates admitted a battle (its reveal), an empty read is lag, not absence: the
          // attempt stays unresolved — Check again or Enter — and is never acknowledged as clear (review W4-6).
          apply({ type: T.RECONCILIATION_RESULT, ...tag, status: admitted ? RECONCILIATION.ERROR : RECONCILIATION.CLEAR });
          return;
        }
        const battle = found.battle;
        apply({ type: T.RECONCILIATION_RESULT, ...tag, status: RECONCILIATION.FOUND, battle: { id: battle?.id, ownerId: battle?.ownerId }, admitted });
        const now2 = state.attempt;
        if (now2?.phase === ATTEMPT_PHASES.CREATED && battle?.ownerId === r.capturedUid) {
          recovered = { attemptId: a.attemptId, battle };
        } else if (now2?.phase === ATTEMPT_PHASES.EXISTING && battle?.ownerId === r.capturedUid) {
          hydration = { key: `${r.capturedUid}:${r.authEpoch}:${a.attemptId}:${battle.id}`, attemptId: a.attemptId, battleId: battle.id, status: EXISTING_READ.FOUND, battle, retryAt: null };
        }
        // An uncertain answer refreshed nothing (FRB-8); the reconciliation that finds its owned battle — existing
        // or recovered — is the poll's trigger instead (spec §6; fix-round review RF-2).
        if (now2?.phase === ATTEMPT_PHASES.CREATED || now2?.phase === ATTEMPT_PHASES.EXISTING) refreshActive();
        changed();
      }, (err) => {
        if (!isCurrent()) return;
        console.warn('[DeployAttempt] the deployment check could not complete:', err?.message || err);
        apply({ type: T.RECONCILIATION_RESULT, ...tag, status: RECONCILIATION.ERROR });
      });
  }

  /** The ceremony's own conclusion for an uncertain attempt, as its phase reports it. */
  function clearFromCeremony() {
    const a = state.attempt;
    if (!a || a.phase !== ATTEMPT_PHASES.UNCERTAIN || a.reconciliationStatus === RECONCILIATION.PENDING) return;
    const tag = { attemptId: a.attemptId, attemptEpoch: a.attemptEpoch };
    apply({ type: T.RECONCILIATION_PENDING, ...tag });
    apply({ type: T.RECONCILIATION_RESULT, ...tag, status: RECONCILIATION.CLEAR });
  }

  function phaseChanged({ attemptId, phase, errorTone }) {
    const a = state.attempt;
    // Stale and repeated reports change nothing (StrictMode echoes; a remounted ceremony's earlier one).
    if (!a || attemptId !== a.attemptId || !run || run.attemptId !== a.attemptId) return;
    apply({ type: T.CEREMONY_PHASE, attemptId, attemptEpoch: a.attemptEpoch, phase, errorTone });
    if (a.phase !== ATTEMPT_PHASES.UNCERTAIN) return;
    // The preserved recovery gates admitted a battle: read it once to learn its id (spec §5.2 "recovered").
    if (phase === CEREMONY_PHASES.REVEAL) reconcile({ admitted: true });
    // A server error signal AND an empty verifier read: the ceremony's confirmed "no battle" is a clear result.
    else if (phase === CEREMONY_PHASES.ERROR && errorTone === ERROR_TONES.CONFIRMED) clearFromCeremony();
  }

  // ── the terminal's actions (§5.4) ───────────────────────────────────────────
  function dismiss() {
    const a = state.attempt;
    if (!a || !run || run.attemptId !== a.attemptId) return;
    if (ceremony?.attemptId === a.attemptId) ceremony = { ...ceremony, visible: false };
    // A review-due refusal's primary action is the Forge (spec §5.4), which lives on the hub. Back to hub from its
    // ceremony therefore closes the ceremony and keeps the refusal: the hub shows its line with "Open in the Forge"
    // and Dismiss. Once closed, the Forge action and Dismiss acknowledge it (independent review FRB-2).
    if (a.phase === ATTEMPT_PHASES.REFUSED && a.result?.kind === RESULT_KINDS.REVIEW_DUE && a.ceremonyPhase !== CEREMONY_PHASES.CLOSED) {
      apply({ type: T.CEREMONY_PHASE, attemptId: a.attemptId, attemptEpoch: a.attemptEpoch, phase: CEREMONY_PHASES.CLOSED, errorTone: null });
      changed();
      return;
    }
    const battleId = a.phase === ATTEMPT_PHASES.CREATED ? realId(a.result?.battleId) : null;
    apply({ type: T.DISMISS, attemptId: a.attemptId, attemptEpoch: a.attemptEpoch, at: now() });
    // The matching stash goes with an acknowledged creation (spec §5.4); Enter has already taken it when it entered.
    if (battleId && state.attempt?.phase === ATTEMPT_PHASES.IDLE) {
      try { bridge().clearCeremonyStash?.(battleId); } catch { /* App's */ }
    }
    changed();
  }

  /**
   * The hub's Dismiss (founder ruling Q-S2b-4): acknowledges a refusal that no ceremony shows, its owner + target
   * cooldown kept (spec §5.4). Nothing else: not an open ceremony's refusal (its Back to hub does that), not an
   * uncertain attempt (its guard stands until reconciliation resolves), not a newer attempt a stale press meets.
   */
  function dismissRefusal() {
    const a = state.attempt;
    if (!a || a.phase !== ATTEMPT_PHASES.REFUSED || a.ceremonyPhase !== CEREMONY_PHASES.CLOSED) return;
    dismiss();
  }

  function enterBattle(recoveredBattle = null) {
    const a = state.attempt;
    const r = run;
    if (!a || !r || r.attemptId !== a.attemptId || !currentFor(r)()) return;
    const doc = recoveredBattle?.id ? recoveredBattle : null;
    if (doc && doc.ownerId !== r.capturedUid) return;
    if (doc && a.phase === ATTEMPT_PHASES.UNCERTAIN) {
      // The reveal's Enter carries the battle the preserved gates admitted: the record learns its id here.
      const tag = { attemptId: a.attemptId, attemptEpoch: a.attemptEpoch };
      if (a.reconciliationStatus !== RECONCILIATION.PENDING) apply({ type: T.RECONCILIATION_PENDING, ...tag });
      apply({ type: T.RECONCILIATION_RESULT, ...tag, status: RECONCILIATION.FOUND, battle: { id: doc.id, ownerId: doc.ownerId }, admitted: true });
    }
    const created = state.attempt;
    if (created?.phase !== ATTEMPT_PHASES.CREATED) return;
    // The battle this record names, when it names one: a recovered resolution's, or the handoff's real id. A
    // document carried in must be that battle (review W4-8).
    const expectedId = realId(created.resolution?.battleId) ?? (created.result?.kind === RESULT_KINDS.HANDOFF ? realId(created.result.battleId) : null);
    if (doc && expectedId && doc.id !== expectedId) return;
    const read = hydration?.attemptId === a.attemptId && hydration.status === EXISTING_READ.FOUND ? hydration.battle : null;
    const entry = doc ?? (recovered?.attemptId === a.attemptId ? recovered.battle : null) ?? read;
    if (!entry && created.result?.kind === RESULT_KINDS.HANDOFF) {
      // A creation whose handoff threw built no battle in App: enter the real one by its id, read and owned
      // (§5.2), through the one owned read — its failure is shown, and its Retry waits (review W4-5).
      hydrate({ open: true });
      return;
    }
    // Identity, epoch and the document's owner, again, immediately before the navigation.
    if (!currentFor(r)() || (entry && entry.ownerId !== r.capturedUid)) return;
    try { bridge().onEnterBattle?.(entry); } finally { dismiss(); }
  }

  function openGame(battle = null) {
    const a = state.attempt;
    const r = run;
    if (!a || !r || r.attemptId !== a.attemptId || a.phase !== ATTEMPT_PHASES.EXISTING) return;
    const existingId = realId(a.resolution?.battleId) ?? realId(a.result?.battleId);
    const doc = battle ?? (hydration?.attemptId === a.attemptId && hydration.status === EXISTING_READ.FOUND ? hydration.battle : null);
    // Not read yet, or its read failed: the Retry reads again (when its wait is over) and opens what it finds.
    if (!doc) { hydrate({ open: true }); return; }
    if (!currentFor(r)() || doc.id !== existingId || doc.ownerId !== r.capturedUid) return;
    try { bridge().onOpenBattle?.(doc); } finally { dismiss(); }
  }

  function retry() {
    const a = state.attempt;
    const r = run;
    if (!a || !r || r.attemptId !== a.attemptId) return Promise.resolve({ success: false });
    return startAttempt({ retryOf: { attemptId: a.attemptId, attemptEpoch: a.attemptEpoch, agent: r.agent, ceremonyAgent: r.ceremonyAgent } });
  }

  function deployCommitted(controlId) {
    const c = committed;
    if (!c || c.controlId !== controlId || state.hold?.holdToken !== c.token || state.hold.phase !== HOLD_PHASES.LOCKED) {
      return Promise.resolve({ success: false });
    }
    if (currentHold()?.holdToken !== c.token) return Promise.resolve({ success: false });
    committed = null;
    progress.clear(c.token);
    const started = startAttempt({ hold: c });
    // startAttempt admits (or not) before its first await. A committed hold that started nothing gives its record
    // back, or no control could ever claim again (review W3-4); the hook gives the slot back when this settles.
    if (state.hold?.holdToken === c.token && !state.hold.attemptId) apply({ type: T.HOLD_RELEASED, holdToken: c.token });
    return started;
  }

  function registerInputs(getter) {
    inputsGetter = getter;
    return () => { if (inputsGetter === getter) inputsGetter = null; };
  }

  /** The earliest future cooldown bound of the signed-in owner, for the clock that re-renders when it passes. */
  function nextCooldownEnd() {
    const uid = readAuthIdentity().uid;
    const bounds = Object.values(state.cooldowns?.[uid] || {}).filter((b) => finite(b) && b > now());
    return bounds.length ? Math.min(...bounds) : null;
  }

  function snapshot() {
    if (cached && cached.version === version) return cached;
    cached = Object.freeze({
      version,
      state,
      overlayOpen: overlayOpen(),
      ceremony,
      due,
      hydration,
      evidenceSeen,
      flagDisabled: state.flagDisabled === true,
    });
    return cached;
  }

  return {
    holdSeam,
    beatOwner,
    progress,
    snapshot,
    registerInputs,
    identityChanged,
    setHubEnabled,
    unmount,
    cancelCharging,
    deployCommitted,
    tap: (controlId, { exemptOverlay = false } = {}) => startAttempt({ tap: { controlId, exemptOverlay } }),
    retry,
    dismiss,
    dismissRefusal,
    enterBattle,
    openGame,
    retryExistingRead,
    checkDeployment: () => reconcile({ admitted: false }),
    phaseChanged,
    lastDeployedEvidence,
    evidenceRead,
    nextCooldownEnd,
    tick: changed,
    isRevoked: () => revoked,
    /** Tests only: the ceremony's live list beside the owned document. */
    liveBattlesFor: () => {
      const a = admission();
      const listed = a ? a.liveBattles : [];
      return hydration?.status === EXISTING_READ.FOUND && hydration.battle ? [hydration.battle, ...listed] : listed;
    },
    agentFor: () => run?.agent ?? null,
    ceremonyAgentFor: () => run?.ceremonyAgent ?? null,
  };
}

/**
 * App's root: a pass-through with the hub flag off; the live provider with it on (ruling R-S2b-1). The flag is
 * read once, at mount, through its accessor (R-S2b-8): a cold flag-off load has no attempt to recover and adds
 * nothing. `hubEnabled` is the rows' injected value — a later false is the runtime disable; App passes none.
 */
export function DeployAttemptProvider({ hubEnabled, children, ...bridge }) {
  const injected = typeof hubEnabled === 'boolean';
  const [live] = useState(() => (injected ? hubEnabled : isCommandCenterHubOn()) === true);
  if (!live) return children;
  return <LiveDeployAttemptProvider hubEnabled={injected ? hubEnabled : true} bridge={bridge}>{children}</LiveDeployAttemptProvider>;
}

function LiveDeployAttemptProvider({ hubEnabled, bridge, children }) {
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  const [, setVersion] = useState(0);
  const make = () => createDeployRuntime({
    getBridge: () => bridgeRef.current,
    notify: () => setVersion((v) => v + 1),
  });
  const [rt, setRuntime] = useState(make);
  const snap = rt.snapshot();

  // The identity, from the one source, synchronously at every change (C2), and the root's revocation (§5.4): an
  // unmount revokes the runtime for good. StrictMode's development re-mount runs this cleanup and then this effect
  // again on the same, now revoked, runtime — before any attempt can exist — so that one start makes a fresh runtime.
  useEffect(() => {
    if (rt.isRevoked()) {
      setRuntime(make);
      return undefined;
    }
    const off = subscribeAuthIdentity(() => rt.identityChanged(readAuthIdentity()));
    rt.identityChanged(readAuthIdentity());
    return () => { off(); rt.unmount(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt]);

  // A runtime disable, injected (no live toggle exists).
  useEffect(() => { rt.setHubEnabled(hubEnabled === true); }, [rt, hubEnabled]);

  // The clock re-renders the hub when a known cooldown bound passes, and only while one is pending.
  useEffect(() => {
    const until = rt.nextCooldownEnd();
    if (until === null) return undefined;
    const id = setTimeout(rt.tick, Math.max(0, until - Date.now()) + 25);
    return () => clearTimeout(id);
  }, [rt, snap.version]);

  const value = useMemo(() => Object.freeze({ snapshot: snap, api: rt }), [rt, snap]);

  const a = snap.state.attempt;
  const c = snap.ceremony;
  const showCeremony = Boolean(c?.visible) && a && a.attemptId === c.attemptId && a.phase !== ATTEMPT_PHASES.ORPHANED;

  // While the root ceremony or the due-idea screen is open, everything behind it is inert (spec §10; the S8 review's
  // S8-14, ruling R-S8F-5): every child of <body> but the overlay's own root — the app root, and a hub sheet's portal
  // beneath it too (the Scouting Board's, whose footer may open the due-idea screen over it), through the refcounted
  // primitive the hub's stack holds its marks with, so a sheet still open beneath keeps the app root inert. The effect
  // is keyed on the overlay on screen, so the due screen → ceremony swap (one sequence, `overlayOpen` true throughout)
  // moves the kept root from the one to the other.
  const overlayKey = snap.due ? `due:${snap.due.attemptId}` : showCeremony ? `ceremony:${c.key}` : null;
  const invokerRef = useRef(null);
  useEffect(() => {
    if (!overlayKey) return undefined;
    // The control the sequence was opened from, taken before its focus goes inert (a browser drops focus from an
    // inert subtree); kept through the swap, so the close goes back to it and not to the screen that came between.
    if (invokerRef.current === null) {
      invokerRef.current = { element: document.activeElement };
      // A sequence starts owing nothing: an opener owed from before it is not this sequence's to return to.
      takeOwedFocus();
    }
    const own = overlayRootOf(overlayKey);
    if (own) return inertSiblings(own);
    // An overlay whose root is not in <body> (a stand-in drawn in place): the app root alone, as before.
    const appRoot = document.getElementById('root');
    return appRoot ? inertSiblings(null, [appRoot]) : undefined;
  }, [overlayKey]);
  // On close (the S8 review's S8-15): the mark above is released first — its cleanup runs before this — then focus goes
  // to the opener a hub sheet's close still owes, when there is one (founder ruling Q-S8F-1: the Scouting Board closed
  // itself under the ceremony, so "See what it's eyeing" — the captured invoker was that sheet's own footer, gone or still
  // leaving); else to the captured invoker; else, gone or disabled, to the hub's shell. The overlay's own restore ran while
  // the page was still inert, so this is the one that lands.
  useEffect(() => {
    if (overlayKey || invokerRef.current === null) return;
    const invoker = invokerRef.current.element;
    invokerRef.current = null;
    if (tryFocus(takeOwedFocus())) return;
    if (invoker && invoker !== document.body && tryFocus(invoker)) return;
    tryFocus(document.querySelector('[data-hub="shell"]'));
  }, [overlayKey]);
  const agent = rt.agentFor();
  const extras = rt.ceremonyAgentFor();
  // The shells' own expressions (CommandDashboard.jsx, CommandDashboardDesktop.jsx), over the attempt's agent.
  const accent = agent?.primaryColor || agent?.avatarColors?.[0] || CMD.teal;
  const agentName = agent?.name || bridge.userName || 'your agent';
  const offerRetry = showCeremony && !snap.flagDisabled;
  // The deploy target's recorded lastDeployedAt is the cooldown's evidence (spec §5.4): read while the attempt
  // stands, and after its acknowledgment until a bound for its owner and target is known, if its request reached
  // the server — a stamp that lands after a quick dismissal is not lost (review W4-7).
  const unacknowledged = a && a.phase !== ATTEMPT_PHASES.IDLE && a.phase !== ATTEMPT_PHASES.ORPHANED;
  const boundUnknown = a && a.phase === ATTEMPT_PHASES.IDLE && a.result?.postIssued !== false
    && cooldownFor(snap.state, a.capturedUid, a.targetAgentId).cooldownUntil === null;
  // Once the target's document has been read and no stamp can still arrive for this attempt, the listener ends
  // (independent review FRB-7).
  const exhausted = a && snap.evidenceSeen === a.attemptId && !stampCanArrive(a);
  const evidenceTarget = (unacknowledged || boundUnknown) && !exhausted && realId(a.targetAgentId) ? a.targetAgentId : null;

  return (
    <DeployAttemptContext.Provider value={value}>
      {children}
      {evidenceTarget && (
        <TargetEvidence key={`${a.attemptId}:${evidenceTarget}`} rt={rt} attemptId={a.attemptId} targetAgentId={evidenceTarget} uid={a.capturedUid} authEpoch={a.authEpoch} />
      )}
      {snap.due && <DueIdeaInterstitial key={snap.due.attemptId} due={snap.due.due} accent={accent} onProceed={snap.due.proceed} />}
      {showCeremony && (
        <RootCeremony
          rt={rt}
          ceremony={c}
          attempt={a}
          agent={agent}
          accent={accent}
          agentName={agentName}
          directiveCount={extras?.directiveCount || 0}
          offerRetry={offerRetry}
          hydration={snap.hydration}
        />
      )}
    </DeployAttemptContext.Provider>
  );
}

/**
 * The deploy target's recorded lastDeployedAt, from the target's own document (decide.js stamps it on a creation
 * and on an existing answer), as cooldown evidence for its owner and target. The same listener the ceremony keeps.
 */
function TargetEvidence({ rt, attemptId, targetAgentId, uid, authEpoch }) {
  const { lastDeployedAt, targetKnown } = useDeployTargetProgress(targetAgentId);
  useEffect(() => {
    if (!targetKnown) return;
    const ms = lastDeployedAtMs(lastDeployedAt, Date.now());
    if (ms !== null) rt.lastDeployedEvidence({ uid, authEpoch, targetAgentId, lastDeployedAtMs: ms });
    rt.evidenceRead(attemptId);
  }, [rt, attemptId, targetKnown, lastDeployedAt, uid, authEpoch, targetAgentId]);
  return null;
}

/**
 * Can the deploy target's lastDeployedAt still change for this attempt? decide.js stamps it on a creation and on
 * an existing answer, so while a request is out, and after any answer but a refusal, it can. A refusal is
 * answered before the decision write and stamps nothing (independent review FRB-7) — but for two 429s the stamp
 * that matters is another deploy's: the in-progress one's still running, and the 2-minute cooldown's already
 * written, which a stale or empty first snapshot may not show yet. Those keep listening until the bound is known
 * (fix-round review RF-1). `'cooldown'` is agentDeploy.js's REFUSAL_REASONS_429 reason for that 429.
 */
function stampCanArrive(a) {
  if (a.phase === ATTEMPT_PHASES.SUBMITTING) return true;
  const kind = a.result?.kind;
  if (kind === RESULT_KINDS.REFUSED || kind === RESULT_KINDS.REVIEW_DUE) {
    const evidence = a.result?.evidence;
    return isInProgressRefusal(evidence) || evidence?.reason === 'cooldown';
  }
  return true;
}

/** The hub attempt's ceremony, at the root: the shipped ceremony, its callbacks through the provider's checks. */
function RootCeremony({ rt, ceremony, attempt, agent, accent, agentName, directiveCount, offerRetry, hydration }) {
  // The existing terminal's Retry goes through the owned read, with that read's wait when it failed (Q-S2b-5).
  const read = hydration?.attemptId === attempt.attemptId ? hydration : null;
  const retryAt = read?.status === EXISTING_READ.ERROR && Number.isFinite(read.retryAt) ? read.retryAt : null;
  return (
    <DeployCeremony
      key={ceremony.key}
      attemptId={ceremony.attemptId}
      onPhaseChange={rt.phaseChanged}
      agent={agent}
      accent={accent}
      agentName={agentName}
      directiveCount={directiveCount}
      deployResult={ceremony.deployResult}
      targetAgentId={ceremony.targetAgentId}
      liveBattles={rt.liveBattlesFor()}
      onEnterBattle={rt.enterBattle}
      onOpenBattle={rt.openGame}
      onDismiss={rt.dismiss}
      onRetry={offerRetry && retryInPrinciple(attempt) ? rt.retry : undefined}
      ownedRead={{ retry: rt.retryExistingRead, retryAt }}
    />
  );
}

/**
 * The ceremony offers Retry whenever the record's phase admits one in principle (spec §5.4): a refusal whose
 * action is a Retry (a review-due refusal's is the Forge), or an uncertain attempt whose reconciliation came back
 * clear. So, like the shells, it shows "Try again" or its countdown under a cooldown (independent review FRB-6).
 * The press re-asks the hub's table (`retry` → startAttempt) and starts nothing while anything blocks it. An
 * unresolved uncertainty has no Retry — Check deployment first — and nothing is ever replayed on its own.
 */
function retryInPrinciple(attempt) {
  if (attempt.phase === ATTEMPT_PHASES.REFUSED) return attempt.result?.kind !== RESULT_KINDS.REVIEW_DUE;
  return attempt.phase === ATTEMPT_PHASES.UNCERTAIN && attempt.reconciliationStatus === RECONCILIATION.CLEAR;
}
